import type { PaginatedGetUserVerificationResponse } from '@/types/onboarding/OnboardingStatus';
import type { UserValidationPayload } from '@/types/onboarding/UserValidationPayload';
import { VerificationSource } from '@/types/enums/VerificationSource';
import { VerificationStatus } from '@/types/enums/VerificationStatus';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useSignStore } from '@/stores/sign.ts';
import { AuthorityService } from '@/types/enums/AuthorityService';
import { useApi } from '@/composables/useApi.ts';
import { formatBySeparator } from '@deepcloud/deep-ui-utils';

export const useDeepAdminInternalStore = defineStore(
  'deepadmin-internal',
  () => {
    const signStore = useSignStore();
    const { deepAdminBackendAPI } = useApi();
    const fetchUserVerificationPending = ref(false);
    const startUserVerificationPending = ref(false);
    const onboardingStatus = ref<PaginatedGetUserVerificationResponse>();
    const isRasVerified = ref(false);
    const signeeIssues = ref();

    const userValidations = computed(() => {
      return onboardingStatus.value?.items;
    });

    const userValidationURL = computed<string>(() => {
      const match = userValidations.value?.find(
        (validationItem) =>
          validationItem?.source === VerificationSource.DEEP_ID
      );
      if (!match) return '';
      return match?.url || '';
    });

    const userValidationVoucher = computed<string | null>(() => {
      const match = userValidations.value?.find(
        (validationItem) =>
          validationItem?.source === VerificationSource.DEEP_ID
      );
      if (!match) return null;
      return match && match?.voucher ? formatBySeparator(match.voucher) : null;
    });

    const isVerified = computed(() => {
      return userValidations.value?.some(
        (uv) => uv.status === VerificationStatus.VERIFIED
      );
    });

    const isInProgress = computed(() => {
      return userValidations.value?.some(
        (uv) => uv.status === VerificationStatus.IN_PROGRESS
      );
    });

    const isNotVerifiedOrInProgress = computed(() => {
      return !isVerified.value && !isInProgress.value;
    });

    const isDeepIDVerification = computed(() => {
      return userValidations.value?.some(
        (uv) => uv.source === VerificationSource.DEEP_ID
      );
    });

    const isDeepIDVerified = computed(() => {
      return userValidations.value?.some(
        (uv) =>
          uv.source === VerificationSource.DEEP_ID &&
          uv.status === VerificationStatus.VERIFIED
      );
    });

    const shouldStartValidation = computed(() => {
      return (
        userValidations.value === null ||
        isNotVerifiedOrInProgress.value ||
        !isDeepIDVerification.value
      );
    });

    async function fetchUserVerification(userId: string) {
      try {
        fetchUserVerificationPending.value = true;
        const response =
          await deepAdminBackendAPI.value.usersVerifications.get(userId);
        onboardingStatus.value = response.data;
        if (isDeepIDVerification.value && isDeepIDVerified.value) {
          signStore.enableAuthorityService(AuthorityService.DEEP_ID);
        }
        fetchUserVerificationPending.value = false;
        return Promise.resolve();
      } catch (error) {
        fetchUserVerificationPending.value = false;
        return Promise.reject(error);
      }
    }

    async function startUserVerification(
      userId: string,
      payload: UserValidationPayload
    ) {
      try {
        startUserVerificationPending.value = true;

        const response =
          await deepAdminBackendAPI.value.usersVerifications.create(userId, {
            ...payload
          });
        startUserVerificationPending.value = false;
        return Promise.resolve(response);
      } catch (error) {
        startUserVerificationPending.value = false;
        return Promise.reject(error);
      }
    }

    return {
      // State
      fetchUserVerificationPending,
      onboardingStatus,
      startUserVerificationPending,
      isRasVerified,
      signeeIssues,

      // Getters
      isDeepIDVerification,
      isInProgress,
      isNotVerifiedOrInProgress,
      isVerified,
      isDeepIDVerified,
      shouldStartValidation,
      userValidations,
      userValidationURL,
      userValidationVoucher,

      // Actions
      fetchUserVerification,
      startUserVerification
    };
  }
);
